import { useEffect, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";

import Input from "../elements/Input";
import Switch from "../elements/Switch";
import EditorToolbar from "./EditorToolbar";
import Availability from "../ui/Availability";
import Visibility from "../ui/Visibility";
import Select from "../elements/Select";
import Logic from "../ui/Logic";
import Options from "../ui/Options";
import Variability from "../ui/Variability";
import DescriptionImageGallery from "../ui/DescriptionImageGallery";
import RichTextEditorModal from "../modals/RichTextEditorModal";

const questionSchema = {
  id: null,
  surveyId: null,
  surveySectionId: null,
  name: "",
  name_fr: "",
  questionNumber: null,
  isActive: true,
  showToClient: true,
  requiresQA: false,
  isRequired: true,
  availability: [],
  visibility: [],
  controlledBy: null,
  questionType: "text",
  options: null,
  config: null,
  minValue: null,
  maxValue: null,
};

const SurveyQuestionEditor = ({
  surveyId,
  surveySectionId,
  question,
  onSave,
  onDelete,
  changesMade,
  onRefresh,
  canDelete = false,
}) => {
  const {
    getLabel,
    questionTypes,
    imageResolution,
    imageNumber,
    imageType,
    previousAnswerTypes,
  } = useLocalization();
  const { getFromServer, updateRecord, deleteRecord } = useData();

  const [questionData, setQuestionData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showDescriptionEditor, setShowDescriptionEditor] = useState(false);
  const [showDescriptionEditorFr, setShowDescriptionEditorFr] = useState(false);

  useEffect(() => {
    getQuestions();
  }, [question]);

  const getQuestions = async () => {
    const questionList = await getFromServer({
      entityName: "SurveyQuestion",
      filter: { surveySectionId },
    });

    // Ensure questionList is always an array
    const safeList = Array.isArray(questionList) ? questionList : [];

    if (question) {
      setQuestionData(question);
    } else {
      setQuestionData({
        ...questionSchema,
        surveyId,
        surveySectionId,
        questionNumber: safeList.length,
      });
    }

    setQuestions(safeList);
  };

  const handleChange = async (field, newValue) => {
    setQuestionData((current) => {
      let updated = current;

      if (field.includes(".")) {
        const objField = field.split(".");
        let objName = objField[0];
        let fieldName = objField[1];
        let obj = {};

        if (objName in current) {
          obj = { ...current[objName] };
        }

        obj[fieldName] = newValue;

        updated = { ...current, [objName]: obj };
      } else {
        updated = { ...current, [field]: newValue };
      }

      // Auto-enable requiresQA for distribution and image questions
      if (
        field === "questionType" &&
        (newValue === "distribution" || newValue === "image")
      ) {
        updated = { ...updated, requiresQA: true };
      }

      return updated;
    });
    changesMade(true);

    if (
      field === "questionType" &&
      newValue === "variability" &&
      !question?.id
    ) {
      await handleSave(false);
    }
  };

  const handleSave = async (closeAfter = true) => {
    let newData;

    if (!question || !question.id) {
      if (questionData.questionType === "image") {
        const imageNumber = questionData?.config?.imageNumber
          ? questionData.config.imageNumber
          : "single";
        const isMulti = imageNumber === "multiple";
        const minImages = isMulti
          ? (questionData?.config?.minImages ?? null)
          : questionData?.isRequired
            ? 1
            : 0;
        const maxImages = isMulti
          ? (questionData?.config?.maxImages ?? null)
          : questionData?.isRequired
            ? 1
            : 1;
        newData = {
          ...questionData,
          surveyId,
          surveySectionId,
          questionNumber: questions.length,
          config: {
            resolution: questionData?.config?.resolution
              ? questionData.config.resolution
              : "medium",
            imageType: questionData?.config?.imageType
              ? questionData.config.imageType
              : "standard",
            imageNumber,
            minImages,
            maxImages,
          },
        };
      } else {
        newData = {
          ...questionData,
          surveyId,
          surveySectionId,
          questionNumber: Array.isArray(questions) ? questions.length : 0,
        };
      }
    } else {
      if (questionData.questionType === "image") {
        const imageNumber = questionData?.config?.imageNumber
          ? questionData.config.imageNumber
          : "single";
        const isMulti = imageNumber === "multiple";
        const minImages = isMulti
          ? (questionData?.config?.minImages ?? null)
          : questionData?.isRequired
            ? 1
            : 0;
        const maxImages = isMulti
          ? (questionData?.config?.maxImages ?? null)
          : questionData?.isRequired
            ? 1
            : 1;
        newData = {
          ...questionData,
          config: {
            resolution: questionData?.config?.resolution
              ? questionData.config.resolution
              : "medium",
            imageType: questionData?.config?.imageType
              ? questionData.config.imageType
              : "standard",
            imageNumber,
            minImages,
            maxImages,
          },
        };
      } else {
        newData = { ...questionData };
      }
    }

    if (newData.controlledBy && !newData.controlledBy.questionId) {
      newData.controlledBy = null;
    }

    const normalizeLimit = (val) => {
      if (val === undefined || val === null || val === "") return null;
      const numeric = Number(val);
      return Number.isFinite(numeric) ? numeric : null;
    };

    if (["number", "currency"].includes(newData.questionType)) {
      newData.minValue = normalizeLimit(newData.minValue);
      newData.maxValue = normalizeLimit(newData.maxValue);
      if (
        newData.minValue !== null &&
        newData.maxValue !== null &&
        newData.minValue > newData.maxValue
      ) {
        throw new Error("Minimum value cannot exceed maximum value");
      }
    } else {
      newData.minValue = null;
      newData.maxValue = null;
    }

    await updateRecord("SurveyQuestion", newData);

    if (closeAfter) {
      onSave();
    } else {
      await onRefresh();
    }
  };

  const handleDelete = async () => {
    if (question?.id) {
      await deleteRecord("SurveyQuestion", question.id);
      if (onDelete) {
        onDelete();
      } else {
        onSave();
      }
    }
  };

  if (!questionData) return;

  return (
    <>
      <Box
        width="100%"
        height="100%"
      >
        <EditorToolbar
          onSave={handleSave}
          onDelete={canDelete ? handleDelete : null}
        />
        <Stack
          spacing={1}
          padding="10px"
          height="calc(100% - 60px)"
          width="calc(100% - 20px)"
          overflow="auto"
        >
          <Input
            id="name"
            label={getLabel("name")}
            value={questionData.name}
            onChange={(newValue) => handleChange("name", newValue)}
            required
            fullwidth
            multiline
          />
          <Input
            id="name_fr"
            label={getLabel("name_fr")}
            value={questionData.name_fr}
            onChange={(newValue) => handleChange("name_fr", newValue)}
            required
            fullwidth
            multiline
          />

          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
          >
            <Switch
              label={getLabel("active")}
              value={questionData.isActive}
              onChange={(newValue) =>
                handleChange("isActive", Boolean(newValue))
              }
            />
            <Switch
              label={getLabel("isRequired")}
              value={questionData.isRequired}
              onChange={(newValue) =>
                handleChange("isRequired", Boolean(newValue))
              }
            />
            <Switch
              label="Show to Client"
              value={questionData.showToClient}
              onChange={(newValue) =>
                handleChange("showToClient", Boolean(newValue))
              }
            />
            <Switch
              label="Requires QA"
              value={questionData.requiresQA}
              onChange={(newValue) =>
                handleChange("requiresQA", Boolean(newValue))
              }
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
          >
            <Select
              id="questionType"
              label={getLabel("type")}
              value={questionData.questionType}
              onChange={(newValue) => handleChange("questionType", newValue)}
              items={questionTypes}
            />

            <Select
              id="previousAnswer"
              label={getLabel("previousAnswer")}
              value={
                questionData.previousAnswer ? questionData.previousAnswer : "na"
              }
              onChange={(newValue) => handleChange("previousAnswer", newValue)}
              items={previousAnswerTypes}
            />
          </Box>

          {["number", "currency"].includes(questionData?.questionType) ? (
            <Box
              display="flex"
              gap={2}
            >
              <Input
                id="minValue"
                label="Minimum Value"
                type="number"
                value={questionData.minValue ?? ""}
                onChange={(newValue) => handleChange("minValue", newValue)}
                fullwidth
              />
              <Input
                id="maxValue"
                label="Maximum Value"
                type="number"
                value={questionData.maxValue ?? ""}
                onChange={(newValue) => handleChange("maxValue", newValue)}
                fullwidth
              />
            </Box>
          ) : null}

          {["radio", "checkbox", "select", "multiselect"].includes(
            questionData?.questionType,
          ) ? (
            <Options
              label={getLabel("options")}
              value={
                Array.isArray(questionData.options) ? questionData.options : []
              }
              onChange={(newValue) => handleChange("options", newValue)}
            />
          ) : null}

          {questionData.questionType === "image" ? (
            <Box>
              <Box
                sx={{ display: "flex" }}
                gap={1}
              >
                <Select
                  id="resolution"
                  label={getLabel("resolution")}
                  value={
                    questionData?.config?.resolution
                      ? questionData.config.resolution
                      : "medium"
                  }
                  onChange={(newValue) =>
                    handleChange("config.resolution", newValue)
                  }
                  items={imageResolution}
                />
                <Select
                  id="imageType"
                  label="Image type"
                  value={
                    questionData?.config?.imageType
                      ? questionData.config.imageType
                      : "standard"
                  }
                  onChange={(newValue) =>
                    handleChange("config.imageType", newValue)
                  }
                  items={imageType}
                />
              </Box>

              <Box
                sx={{ display: "flex", marginTop: "8px" }}
                gap={1}
              >
                <Select
                  id="imageNumber"
                  label="Image Number"
                  value={
                    questionData?.config?.imageNumber
                      ? questionData.config.imageNumber
                      : "single"
                  }
                  onChange={(newValue) =>
                    handleChange("config.imageNumber", newValue)
                  }
                  items={imageNumber}
                  fullwidth
                />
                {questionData?.config?.imageNumber === "multiple" ? (
                  <Box
                    sx={{ display: "flex" }}
                    gap={1}
                  >
                    <Input
                      id="minImages"
                      label="Minimum"
                      type="number"
                      value={
                        questionData?.config?.minImages
                          ? questionData.config.minImages
                          : ""
                      }
                      onChange={(newValue) =>
                        handleChange("config.minImages", newValue)
                      }
                      fullwidth
                    />
                    <Input
                      id="maxImages"
                      label="Maximum"
                      type="number"
                      value={
                        questionData?.config?.maxImages
                          ? questionData.config.maxImages
                          : ""
                      }
                      onChange={(newValue) =>
                        handleChange("config.maxImages", newValue)
                      }
                      fullwidth
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : null}

          {questionData.questionType === "variability" && (
            <Variability
              surveyQuestionId={questionData.id}
              variability={questionData?.variability || null}
              onChange={(newValue) => handleChange("variability", newValue)}
            />
          )}

          <Box>
            <Logic
              entityName="SurveyQuestion"
              surveyId={surveyId}
              question={questionData}
              value={questionData.controlledBy}
              onChange={(newValue) => handleChange("controlledBy", newValue)}
            />
          </Box>

          <Availability
            value={questionData.availability}
            onChange={(newValue) => handleChange("availability", newValue)}
            label={getLabel("availability")}
          />

          <Visibility
            label={getLabel("visibility")}
            value={questionData.visibility}
            onChange={(newValue) => handleChange("visibility", newValue)}
          />

          {/* Description Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: "bold" }}>
                Description (Optional)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Description Text - English */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1 }}
                  >
                    Description Text (English)
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "3px",
                      padding: "10px",
                      cursor: "pointer",
                      minHeight: "40px",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                    onClick={() => setShowDescriptionEditor(true)}
                  >
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        "& p": { margin: 0 },
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          questionData?.descriptionText?.trim().length > 0
                            ? questionData.descriptionText
                            : "<em>Click to add description text...</em>",
                      }}
                    />
                  </Box>
                </Box>

                {/* Description Images - English */}
                <DescriptionImageGallery
                  entityType="surveyquestion"
                  entityId={questionData?.id}
                  images={questionData?.descriptionImages || []}
                  images_fr={questionData?.descriptionImages_fr || []}
                  onChange={(newValue) => {
                    handleChange("descriptionImages", newValue.images);
                    handleChange("descriptionImages_fr", newValue.images_fr);
                  }}
                  language="en"
                />

                {/* Description Text - French */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1 }}
                  >
                    Description Text (French)
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "3px",
                      padding: "10px",
                      cursor: "pointer",
                      minHeight: "40px",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                    onClick={() => setShowDescriptionEditorFr(true)}
                  >
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        "& p": { margin: 0 },
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          questionData?.descriptionText_fr?.trim().length > 0
                            ? questionData.descriptionText_fr
                            : "<em>Cliquez pour ajouter une description...</em>",
                      }}
                    />
                  </Box>
                </Box>

                {/* Description Images - French */}
                <DescriptionImageGallery
                  entityType="surveyquestion"
                  entityId={questionData?.id}
                  images={questionData?.descriptionImages || []}
                  images_fr={questionData?.descriptionImages_fr || []}
                  onChange={(newValue) => {
                    handleChange("descriptionImages", newValue.images);
                    handleChange("descriptionImages_fr", newValue.images_fr);
                  }}
                  language="fr"
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Box>

      <RichTextEditorModal
        html={questionData?.descriptionText || ""}
        open={showDescriptionEditor}
        onClose={() => setShowDescriptionEditor(false)}
        onChange={(newValue) => handleChange("descriptionText", newValue)}
      />

      <RichTextEditorModal
        html={questionData?.descriptionText_fr || ""}
        open={showDescriptionEditorFr}
        onClose={() => setShowDescriptionEditorFr(false)}
        onChange={(newValue) => handleChange("descriptionText_fr", newValue)}
      />
    </>
  );
};

export default SurveyQuestionEditor;
